// src/pages/dashboard/ConnectWebsite.js
// "Connect Your Website" — profile link + booking widget setup.
// Accessible post-onboarding (for hasExistingWebsite users) and from Settings.

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Copy, Check, ExternalLink } from 'lucide-react';
import { auth, db } from '../../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { generateSlugSuggestion, isSlugAvailable, claimSlug } from '../../utils/slug-utils';

const ConnectWebsite = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isPostOnboarding = searchParams.get('from') === 'onboarding';

  const [slug, setSlug] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkCopied, setLinkCopied] = useState(false);

  // Load practitioner slug
  useEffect(() => {
    let done = false;
    const timeout = setTimeout(() => { if (!done) { done = true; setLoading(false); } }, 5000);

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (done) return;
      done = true;
      clearTimeout(timeout);

      if (!user) { setLoading(false); return; }

      try {
        const practDoc = await getDoc(doc(db, 'practitioners', user.uid));
        if (practDoc.exists()) {
          const data = practDoc.data();
          if (data.slug) {
            setSlug(data.slug);
          } else {
            // Auto-generate and claim a slug from display name
            const displayName = data.profile?.displayName
              || data.displayName
              || JSON.parse(localStorage.getItem('practitioner_profile') || '{}').displayName
              || '';
            if (displayName) {
              const suggestion = generateSlugSuggestion(displayName);
              if (suggestion) {
                try {
                  const available = await isSlugAvailable(suggestion);
                  if (available) {
                    await claimSlug(suggestion, user.uid);
                    setSlug(suggestion);
                  } else {
                    // Try with a number suffix
                    const withSuffix = suggestion + '-1';
                    const suffixAvail = await isSlugAvailable(withSuffix);
                    if (suffixAvail) {
                      await claimSlug(withSuffix, user.uid);
                      setSlug(withSuffix);
                    }
                  }
                } catch (slugErr) {
                  console.warn('⚠️ Auto-slug claim failed:', slugErr);
                }
              }
            }
          }
        }
      } catch (err) {
        console.warn('ConnectWebsite: failed to load practitioner:', err.message);
      }
      setLoading(false);
    });

    return () => { done = true; clearTimeout(timeout); unsub(); };
  }, []);

  const profileUrl = slug ? `https://divineframeworks.com/site/${slug}` : null;

  const handleCopyLink = async () => {
    if (!profileUrl) return;
    try {
      await navigator.clipboard.writeText(profileUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = profileUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20" style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <div className="max-w-2xl mx-auto px-4 py-6">

        {/* Breadcrumb — only show when not post-onboarding */}
        {!isPostOnboarding && (
          <nav className="flex items-center gap-2 text-sm mb-6" aria-label="Breadcrumb">
            <Link to="/dashboard/practitioner" className="text-purple-600 hover:text-purple-700 font-medium transition-colors">
              Dashboard
            </Link>
            <span className="text-gray-400">/</span>
            <Link to="/dashboard/settings" className="text-purple-600 hover:text-purple-700 font-medium transition-colors">
              Settings
            </Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 font-medium">Connect Your Website</span>
          </nav>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#111827', marginBottom: '6px' }}>
            Connect your website
          </h1>
          <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.5 }}>
            Two ways to send clients to your DF bookings:
          </p>
        </div>

        {/* Option 1: Simple link */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
          <h2 style={{ fontSize: '15px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>
            Option 1: Simple link
          </h2>
          <p style={{ fontSize: '13px', color: '#6B7280', lineHeight: 1.5, marginBottom: '16px' }}>
            Copy this link and add it anywhere on your website — a button, menu item, or text link.
          </p>

          {profileUrl ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 14px', background: '#f9fafb',
              border: '1px solid #E5E7EB', borderRadius: '8px',
            }}>
              <code style={{
                flex: 1, fontSize: '13px', color: '#374151',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {profileUrl}
              </code>
              <span
                role="button"
                tabIndex={0}
                onClick={handleCopyLink}
                onKeyDown={(e) => e.key === 'Enter' && handleCopyLink()}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  padding: '6px 14px', background: linkCopied ? '#D1FAE5' : '#7c3aed',
                  color: linkCopied ? '#065F46' : 'white',
                  borderRadius: '6px', fontSize: '12px', fontWeight: 600,
                  cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
                  transition: 'background 0.2s ease',
                }}
              >
                {linkCopied ? <><Check style={{ width: '14px', height: '14px' }} /> Copied!</> : <><Copy style={{ width: '14px', height: '14px' }} /> Copy link</>}
              </span>
            </div>
          ) : (
            <p style={{ fontSize: '13px', color: '#9CA3AF', fontStyle: 'italic' }}>
              Your profile link will be available once your profile URL is set up.
            </p>
          )}
        </div>

        {/* Option 2: Booking button */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <h2 style={{ fontSize: '15px', fontWeight: 700, color: '#111827', margin: 0 }}>
              Option 2: Booking button
            </h2>
            <span style={{
              padding: '2px 8px', background: '#f5f3ff', color: '#7c3aed',
              fontSize: '11px', fontWeight: 600, borderRadius: '20px',
            }}>
              Recommended
            </span>
          </div>
          <p style={{ fontSize: '13px', color: '#6B7280', lineHeight: 1.5, marginBottom: '16px' }}>
            Add a styled booking button to your site with one line of code. Customize the text, color, and shape to match your brand.
          </p>
          <span
            role="button"
            tabIndex={0}
            onClick={() => navigate('/dashboard/settings/widget')}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/dashboard/settings/widget')}
            style={{
              display: 'inline-block',
              padding: '10px 24px',
              background: '#7c3aed',
              color: 'white',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            Set up booking button →
          </span>
        </div>

        {/* Reminder note */}
        <div style={{
          padding: '14px 16px', background: '#f9fafb',
          border: '1px solid #E5E7EB', borderRadius: '10px',
          marginBottom: '24px',
        }}>
          <p style={{ fontSize: '13px', color: '#6B7280', margin: 0, lineHeight: 1.5 }}>
            You can always find these options in your dashboard under{' '}
            <strong>Settings → Connect Your Website</strong>.
          </p>
        </div>

        {/* Go to dashboard */}
        <div style={{ textAlign: 'center' }}>
          <span
            role="button"
            tabIndex={0}
            onClick={() => navigate('/dashboard/practitioner')}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/dashboard/practitioner')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '11px 28px',
              background: isPostOnboarding ? '#7c3aed' : 'white',
              color: isPostOnboarding ? 'white' : '#7c3aed',
              border: isPostOnboarding ? 'none' : '1.5px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            Go to my dashboard <ExternalLink style={{ width: '14px', height: '14px' }} />
          </span>
        </div>

      </div>
    </div>
  );
};

export default ConnectWebsite;
